import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import {
    ArrowLeft, User, Pencil, Send, UserCheck, ExternalLink, FileText, Loader2,
    Mail, Phone, MapPin, Megaphone, ClipboardList, Trash2, BellPlus, type LucideIcon,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ConfirmDeleteDialog } from '../components/ui/confirm-delete-dialog';
import { cn } from '../lib/cn';
import { useStore } from '../store/useStore';
import { leadService } from '../lib/leadService';
import {
    Lead, LeadStage, LEAD_STAGES, LEAD_STAGE_LABELS, LEAD_SOURCE_LABELS, LEAD_SOURCE_ICONS,
    LEAD_LOST_REASON_LABELS, LEAD_SOURCE_COLORS,
} from '../types/leads.types';
import { LeadTimeline } from '../components/leads/LeadTimeline';
import { LeadFormDialog } from '../components/leads/LeadFormDialog';
import { SendLeadEmailModal } from '../components/leads/SendLeadEmailModal';
import { LeadQuickActions } from '../components/leads/LeadQuickActions';
import { LeadNextAction } from '../components/leads/LeadNextAction';
import { LostReasonDialog } from '../components/leads/LostReasonDialog';
import { LeadNotesCard } from '../components/leads/LeadNotesCard';
import { BadgeMail } from '../components/leads/BadgeMail';

function initialsOf(name: string) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

function Fact({ icon: Icon, label, children }: { icon: LucideIcon; label: string; children: React.ReactNode }) {
    return (
        <div className="flex items-start gap-3 px-4 py-3">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
                <Icon className="h-4 w-4" />
            </span>
            <div className="min-w-0">
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</dt>
                <dd className="mt-0.5 text-sm font-medium text-gray-900">{children}</dd>
            </div>
        </div>
    );
}

export default function LeadDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { addNotification } = useStore();
    const [lead, setLead] = useState<Lead | null>(null);
    const [loading, setLoading] = useState(true);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isEmailOpen, setIsEmailOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [confirmConvert, setConfirmConvert] = useState(false);
    const [converting, setConverting] = useState(false);
    const [generatingOffer, setGeneratingOffer] = useState(false);
    const [lostOpen, setLostOpen] = useState(false);
    const [lostBusy, setLostBusy] = useState(false);

    const load = async () => {
        if (!id) return;
        setLoading(true);
        try {
            setLead(await leadService.getLead(id));
        } catch (error) {
            console.error('Error loading lead:', error);
            navigate('/leads');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [id]);

    const applyStage = async (
        stage: LeadStage,
        patch: Partial<Pick<Lead, 'lost_reason' | 'next_action' | 'next_action_at'>> = {}
    ) => {
        if (!lead) return;
        const prev = lead.stage;
        setLead({ ...lead, stage, ...patch });
        try {
            await leadService.updateStage(lead.id, stage, prev, patch);
        } catch (error) {
            console.error(error);
            setLead({ ...lead, stage: prev });
            addNotification({ type: 'error', title: 'Errore', message: 'Impossibile aggiornare lo stato' });
        }
    };

    const handleStageChange = async (stage: LeadStage) => {
        if (!lead) return;
        if (stage === 'perso') { setLostOpen(true); return; }
        await applyStage(stage, lead.stage === 'perso' ? { lost_reason: null } : {});
    };

    const confirmLost = async (reason: string) => {
        setLostBusy(true);
        await applyStage('perso', { lost_reason: reason });
        setLostBusy(false);
        setLostOpen(false);
    };

    const handleDelete = async () => {
        if (!lead) return;
        setIsDeleting(true);
        try {
            await leadService.deleteLead(lead.id);
            addNotification({ type: 'success', title: 'Lead eliminato' });
            navigate('/leads');
        } catch (error) {
            console.error(error);
            addNotification({ type: 'error', title: 'Errore eliminazione' });
        } finally {
            setIsDeleting(false);
            setConfirmDelete(false);
        }
    };

    const handleConvert = async () => {
        if (!lead) return;
        setConverting(true);
        try {
            const customerId = await leadService.convertToCustomer(lead);
            addNotification({ type: 'success', title: 'Lead convertito in cliente' });
            navigate(`/customers/${customerId}`);
        } catch (error) {
            console.error(error);
            addNotification({ type: 'error', title: 'Errore conversione', message: 'Impossibile convertire il lead' });
        } finally {
            setConverting(false);
            setConfirmConvert(false);
        }
    };

    const handleGenerateOffer = async () => {
        if (!lead) return;
        setGeneratingOffer(true);
        try {
            const { priceListId } = await leadService.createOfferFromLead(lead);
            addNotification({
                type: 'success',
                title: 'Offerta pronta',
                message: 'Cliente prospect creato. Puoi inserire i prodotti.',
            });
            navigate(`/price-lists?edit=${priceListId}`);
        } catch (error) {
            console.error(error);
            addNotification({
                type: 'error',
                title: 'Errore',
                message: 'Impossibile generare l\'offerta dal lead',
            });
        } finally {
            setGeneratingOffer(false);
        }
    };

    if (loading) return <div className="p-8">Caricamento...</div>;
    if (!lead) return <div className="p-8">Lead non trovato</div>;

    const SourceIcon = LEAD_SOURCE_ICONS[lead.source] || User;
    const title = lead.full_name || lead.company_name || 'Lead senza nome';
    const stageMeta = LEAD_STAGES.find((s) => s.key === lead.stage);
    const canOffer = (lead.stage === 'qualificato' || lead.stage === 'proposta' || lead.converted_customer_id)
        && lead.stage !== 'perso';
    const canConvert = lead.stage !== 'vinto' && lead.stage !== 'perso';
    return (
        <div className="mx-auto w-full max-w-7xl py-2 md:py-6 animate-in fade-in duration-500">
            <ConfirmDeleteDialog
                open={confirmDelete}
                onOpenChange={(open) => !open && setConfirmDelete(false)}
                onConfirm={handleDelete}
                isDeleting={isDeleting}
                title="Elimina Lead"
                description={`Sei sicuro di voler eliminare "${title}"? L'azione è irreversibile.`}
            />
            <ConfirmDeleteDialog
                open={confirmConvert}
                onOpenChange={(open) => !open && setConfirmConvert(false)}
                onConfirm={handleConvert}
                isDeleting={converting}
                title="Converti in Cliente"
                description="Verrà creato un nuovo cliente con i dati di questo lead, che passerà allo stato 'Vinto'. Continuare?"
                confirmLabel="Converti"
                busyLabel="Conversione..."
                confirmClassName="bg-green-600 hover:bg-green-700"
            />

            <Button variant="ghost" className="mb-4 pl-0 hover:pl-2 transition-all" onClick={() => navigate('/leads')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Torna ai lead
            </Button>

            <header className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <div className={cn('h-1.5', stageMeta?.dotClass ?? 'bg-primary-600')} />
                <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex min-w-0 gap-4">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-600 text-lg font-semibold text-white shadow-sm">
                            {initialsOf(title)}
                        </div>
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <h1 className="font-display text-2xl font-semibold tracking-tight text-gray-950 break-words">{title}</h1>
                                <span className={cn('rounded-full px-2.5 py-0.5 text-xs font-semibold', stageMeta?.badgeClass)}>
                                    {LEAD_STAGE_LABELS[lead.stage]}
                                </span>
                            </div>
                            {lead.full_name && lead.company_name && (
                                <p className="mt-0.5 text-sm text-gray-600">{lead.company_name}</p>
                            )}
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                                    <SourceIcon className={cn('h-3.5 w-3.5', LEAD_SOURCE_COLORS[lead.source])} />
                                    {LEAD_SOURCE_LABELS[lead.source]}
                                </span>
                                <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                                    {format(new Date(lead.created_at), 'd MMM yyyy', { locale: it })}
                                </span>
                                {lead.assignee && (
                                    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                                        {lead.assignee.full_name || lead.assignee.email}
                                    </span>
                                )}
                                {lead.stage === 'perso' && lead.lost_reason && (
                                    <span className="rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700">
                                        {LEAD_LOST_REASON_LABELS[lead.lost_reason.split(':')[0]] || lead.lost_reason}
                                    </span>
                                )}
                                {lead.converted_customer_id && (
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/customers/${lead.converted_customer_id}`)}
                                        className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700 hover:bg-primary-100"
                                    >
                                        <ExternalLink className="h-3 w-3" />
                                        {lead.stage === 'vinto' ? 'Cliente' : 'Prospect'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="w-full sm:w-52">
                        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">Stato</label>
                        <Select value={lead.stage} onValueChange={(value) => handleStageChange(value as LeadStage)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {LEAD_STAGES.map((stage) => (
                                    <SelectItem key={stage.key} value={stage.key}>{stage.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-2 px-5 pb-4 sm:grid-cols-6">
                    {LEAD_STAGES.map((stage) => {
                        const on = stage.key === lead.stage;
                        return (
                            <div key={stage.key}>
                                <div className={cn('h-1.5 rounded-full', on ? stage.dotClass : 'bg-gray-100')} />
                                <div className={cn('mt-1 truncate text-[11px]', on ? 'font-semibold text-gray-900' : 'text-gray-400')}>
                                    {stage.label}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="flex flex-col gap-3 border-t border-gray-100 bg-gray-50 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <LeadQuickActions lead={lead} onLogged={load} onEmail={() => setIsEmailOpen(true)} />
                    <div className="flex flex-wrap gap-2">
                        {canOffer && (
                            <Button
                                className="bg-amber-600 text-white hover:bg-amber-700"
                                onClick={handleGenerateOffer}
                                disabled={generatingOffer}
                            >
                                {generatingOffer
                                    ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                                    : <FileText className="mr-1 h-4 w-4" />}
                                {generatingOffer ? 'Generazione...' : 'Genera Offerta'}
                            </Button>
                        )}
                        <Button variant="outline" className="bg-white" onClick={() => setIsEditOpen(true)}>
                            <Pencil className="mr-1 h-4 w-4" /> Modifica
                        </Button>
                        <Button variant="outline" className="bg-white" onClick={() => setIsEmailOpen(true)} disabled={!lead.email}>
                            <Send className="mr-1 h-4 w-4" /> Email
                        </Button>
                        {lead.converted_customer_id && (
                            <Button variant="outline" className="bg-white" onClick={() => navigate(`/customers/${lead.converted_customer_id}`)}>
                                <ExternalLink className="mr-1 h-4 w-4" />
                                {lead.stage === 'vinto' ? 'Apri Cliente' : 'Apri Prospect'}
                            </Button>
                        )}
                        {canConvert && (
                            <Button className="bg-primary-600 text-white hover:bg-primary-700" onClick={() => setConfirmConvert(true)}>
                                <UserCheck className="mr-1 h-4 w-4" /> Converti
                            </Button>
                        )}
                    </div>
                </div>
            </header>

            <div className="mt-4 grid grid-cols-1 items-start gap-4 lg:grid-cols-[22rem_minmax(0,1fr)]">
                <aside className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                    <div className="flex items-center gap-2.5 border-b border-primary-100 bg-primary-50 px-4 py-3">
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-600 text-white">
                            <User className="h-4 w-4" />
                        </span>
                        <span className="text-sm font-semibold text-gray-900">Dettagli</span>
                    </div>
                    <dl className="divide-y divide-gray-100">
                        {lead.email && (
                            <Fact icon={Mail} label="Email">
                                <a href={`mailto:${lead.email}`} className="break-all hover:text-primary-700">{lead.email}</a>
                                <div className="mt-1.5">
                                    <BadgeMail email={lead.email} />
                                </div>
                            </Fact>
                        )}
                        {lead.phone && (
                            <Fact icon={Phone} label="Telefono">
                                <a href={`tel:${lead.phone}`} className="hover:text-primary-700">{lead.phone}</a>
                            </Fact>
                        )}
                        {(lead.city || lead.province) && (
                            <Fact icon={MapPin} label="Luogo">
                                {[lead.city, lead.province].filter(Boolean).join(', ')}
                            </Fact>
                        )}
                        {lead.campaign_name && (
                            <Fact icon={Megaphone} label="Campagna">{lead.campaign_name}</Fact>
                        )}
                        {lead.form_name && (
                            <Fact icon={ClipboardList} label="Modulo">{lead.form_name}</Fact>
                        )}
                    </dl>
                    <div className="border-t border-gray-100 px-4 py-3">
                        <button
                            type="button"
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-400 hover:text-red-700"
                            onClick={() => setConfirmDelete(true)}
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                            Elimina lead
                        </button>
                    </div>
                </aside>

                <div className="flex flex-col gap-4">
                    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                        <div className="flex items-center gap-2.5 border-b border-primary-100 bg-primary-50 px-4 py-3">
                            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-600 text-white">
                                <BellPlus className="h-4 w-4" />
                            </span>
                            <span className="text-sm font-semibold text-gray-900">Prossima azione</span>
                        </div>
                        <div className="p-4">
                            <LeadNextAction lead={lead} onSaved={load} className="[&>label]:hidden" />
                        </div>
                    </section>
                    <section className="flex min-h-[16rem] flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                        <LeadTimeline leadId={lead.id} />
                    </section>
                    {/* Tutto quello che la persona ha compilato nel modulo, più le note del lead */}
                    <LeadNotesCard lead={lead} />
                </div>
            </div>

            <LeadFormDialog
                open={isEditOpen}
                onOpenChange={setIsEditOpen}
                lead={lead}
                onSaved={load}
            />
            <SendLeadEmailModal
                isOpen={isEmailOpen}
                onClose={() => setIsEmailOpen(false)}
                lead={lead}
                onSent={load}
            />
            <LostReasonDialog
                open={lostOpen}
                leadName={lead.full_name || lead.company_name || undefined}
                busy={lostBusy}
                onCancel={() => setLostOpen(false)}
                onConfirm={confirmLost}
            />
        </div>
    );
}
